/**
 * ForgotPasswordScreen — flow reset mot de passe en 2 étapes.
 *
 * Étape 1 : email → ForgotPassword API → Cognito envoie un code
 *           via SES (no-reply@mytenue.com).
 * Étape 2 : code reçu + nouveau mot de passe + confirmation →
 *           ConfirmForgotPassword API → mot de passe mis à jour.
 *
 * Phase 2 (v1.12.118) — nécessite SES configuré dans Cognito User Pool.
 */

import React, { useState } from 'react';
import {
  SafeAreaView, View, Text, TextInput, TouchableOpacity,
  StyleSheet, Alert, ActivityIndicator, ScrollView, Platform, KeyboardAvoidingView,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { showAppAlert } from '../components/AppDialog';
import { rootReplace } from '../utils/rootNavigation';
import { spacing, radius, shadow } from '../theme';
import { useI18n } from '../i18n';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

function isEmailValid(e) {
  return typeof e === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
}
function passwordIssues(p, t) {
  if (typeof p !== 'string' || p.length < 8) return t('err_password_min_length');
  if (!/[a-z]/.test(p)) return t('err_password_lowercase');
  if (!/[A-Z]/.test(p)) return t('err_password_uppercase');
  if (!/[0-9]/.test(p)) return t('err_password_digit');
  return null;
}

export default function ForgotPasswordScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { requestPasswordReset, confirmPasswordReset } = useAuth();
  const { theme } = useTheme();
  const { t } = useI18n();

  // Étape : 1 = saisie email, 2 = saisie code + nouveau mdp
  const [step, setStep] = useState(1);
  const [email,   setEmail]   = useState(route.params?.prefillEmail || '');
  const [code,    setCode]    = useState('');
  const [pwd,     setPwd]     = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy,    setBusy]    = useState(false);
  // v1.12.118 — erreurs inline (champs rouges + message)
  const [errors, setErrors] = useState({}); // { email?, code?, pwd?, confirm? }

  // Reset l'erreur d'un champ dès que l'user retape
  const setField = (key, val, setter) => {
    setter(val);
    if (errors[key]) setErrors(e => ({ ...e, [key]: undefined }));
  };

  const onRequest = async () => {
    const em = email.trim().toLowerCase();
    if (!isEmailValid(em)) {
      setErrors({ email: !em ? t('err_email_required') : t('err_email_invalid') });
      return;
    }
    setErrors({});
    setBusy(true);
    const r = await requestPasswordReset({ email: em });
    setBusy(false);
    // v1.12.930 — email non vérifié → flow vérification d'abord
    if (r?.needsEmailVerification) {
      showAppAlert(
        t('email_not_verified_title') || 'Email non vérifié',
        r?.message || t('email_not_verified_msg') || 'Veuillez vérifier votre email avant de réinitialiser le mot de passe.',
        [{ text: 'OK', style: 'default', onPress: () => navigation.navigate('ConfirmEmail', { email: em, flow: 'verify-email' }) }],
      );
      return;
    }
    if (!r?.success) {
      const errLower = (r?.error || '').toLowerCase();
      // Cas spécial : compte introuvable → message explicite + propose SignUp
      if (errLower.includes('aucun compte') || errLower.includes('introuvable') || errLower.includes('not found')) {
        setErrors({ email: t('err_account_not_found') });
        showAppAlert(
          t('account_not_found_title'),
          t('account_not_found_msg').replace('{email}', em),
          [
            { text: t('cancel'),             style: 'cancel'  },
            { text: t('create_account_link'),style: 'default', onPress: () => setTimeout(() => rootReplace('SignUp', { prefillEmail: em }), 100) },
          ],
        );
        return;
      }
      setErrors({ email: r?.error || t('err_send_code_failed') });
      return;
    }
    // v1.12.118 — vider explicitement le champ code avant de passer à
    // l'étape 2 (parfois un caractère résiduel s'y glissait via autofill).
    setCode('');
    setPwd('');
    setConfirm('');
    setStep(2);
    showAppAlert(
      t('code_sent_title'),
      t('code_sent_msg'),
      [{ text: 'OK', style: 'default' }],
    );
  };

  const onReset = async () => {
    const em = email.trim().toLowerCase();
    const c = (code || '').replace(/\s/g, '');
    const errs = {};
    if (!c)             errs.code     = t('err_code_required');
    else if (c.length < 4) errs.code  = t('err_code_6_digits');
    if (!pwd)           errs.pwd      = t('err_password_required');
    else {
      const issue = passwordIssues(pwd, t);
      if (issue) errs.pwd = issue;
    }
    if (!confirm)               errs.confirm = t('err_confirm_password');
    else if (pwd && pwd !== confirm) errs.confirm = t('err_passwords_mismatch');

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});

    setBusy(true);
    const r = await confirmPasswordReset({ email: em, code: c, newPassword: pwd });
    setBusy(false);
    if (!r?.success) {
      const errMsg = (r?.error || '').toLowerCase();
      if (errMsg.includes('expir')) {
        setErrors({ code: t('err_code_expired_short') });
      } else if (errMsg.includes('incorrect') || errMsg.includes('mismatch')) {
        setErrors({ code: t('err_code_incorrect_short') });
      } else {
        setErrors({ code: r?.error || t('err_reset_failed') });
      }
      return;
    }
    showAppAlert(
      t('password_changed_title'),
      t('password_changed_msg'),
      [{ text: t('sign_in_cta'), style: 'default' }],
    ).then(() => {
      setTimeout(() => rootReplace('Login', { prefillEmail: em }), 100);
    });
  };

  const insets = useSafeAreaInsets();
  return (
    <SafeAreaView style={[s.safe, { backgroundColor: theme.bg }]}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView
        contentContainerStyle={[s.scroll, { paddingTop: spacing.xl + insets.top, paddingBottom: 40 + insets.bottom }]}
        keyboardShouldPersistTaps="handled"
        automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
      >
        <View style={s.header}>
          <Text style={s.icon}>🔑</Text>
          <Text style={[s.title, { color: theme.text }]}>{t('forgot_password_title')}</Text>
          <Text style={[s.subtitle, { color: theme.textFaint }]}>
            {step === 1
              ? t('forgot_step1_subtitle')
              : t('forgot_step2_subtitle')}
          </Text>
        </View>

        {step === 1 && (
          <View style={s.form}>
            <Text style={[s.label, { color: theme.textFaint }]}>{t('email_label')}</Text>
            <TextInput
              value={email}
              onChangeText={(v) => setField('email', v, setEmail)}
              placeholder={t('email_placeholder')}
              placeholderTextColor={theme.textFaint}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              textContentType="emailAddress"
              style={[s.input, { borderColor: theme.bgBorder, color: theme.text, backgroundColor: theme.bgSurface }, errors.email && s.inputError]}
              onSubmitEditing={onRequest}
              returnKeyType="send"
            />
            {errors.email && <Text style={s.errorText}>{errors.email}</Text>}
            <TouchableOpacity
              onPress={onRequest}
              disabled={busy}
              activeOpacity={0.85}
              style={[s.submitBtn, busy && { opacity: 0.6 }]}
            >
              {busy
                ? <ActivityIndicator color="#fff" size="small" />
                : <Text style={s.submitText}>{t('forgot_send_code_cta')}</Text>}
            </TouchableOpacity>
          </View>
        )}

        {step === 2 && (
          <View style={s.form}>
            <Text style={[s.label, { color: theme.textFaint }]}>{t('forgot_code_label')}</Text>
            <TextInput
              value={code}
              onChangeText={(v) => setField('code', v.replace(/\D/g, '').slice(0, 8), setCode)}
              placeholder="000000"
              placeholderTextColor={theme.textFaint}
              keyboardType="number-pad"
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="off"
              textContentType="oneTimeCode"
              style={[s.input, s.codeInputWide, { borderColor: theme.bgBorder, color: theme.text, backgroundColor: theme.bgSurface }, errors.code && s.inputError]}
            />
            {errors.code && <Text style={s.errorText}>{errors.code}</Text>}

            <Text style={[s.label, { color: theme.textFaint }]}>{t('forgot_new_password_label')}</Text>
            <View style={{ position: 'relative' }}>
              <TextInput
                value={pwd}
                onChangeText={(v) => setField('pwd', v, setPwd)}
                placeholder={t('password_placeholder')}
                placeholderTextColor={theme.textFaint}
                secureTextEntry={!showPwd}
                autoCapitalize="none"
                autoCorrect={false}
                textContentType="newPassword"
                style={[s.input, { paddingRight: 70, borderColor: theme.bgBorder, color: theme.text, backgroundColor: theme.bgSurface }, errors.pwd && s.inputError]}
              />
              <TouchableOpacity
                onPress={() => setShowPwd(v => !v)}
                style={s.eyeBtn}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Text style={[s.eyeBtnText, { color: theme.textFaint }]}>{showPwd ? t('hide_password') : t('show_password')}</Text>
              </TouchableOpacity>
            </View>
            {errors.pwd && <Text style={s.errorText}>{errors.pwd}</Text>}

            <Text style={[s.label, { color: theme.textFaint }]}>{t('confirm_password')}</Text>
            <View style={{ position: 'relative' }}>
              <TextInput
                value={confirm}
                onChangeText={(v) => setField('confirm', v, setConfirm)}
                placeholder={t('password_retype_placeholder')}
                placeholderTextColor={theme.textFaint}
                secureTextEntry={!showConfirm}
                autoCapitalize="none"
                autoCorrect={false}
                textContentType="newPassword"
                style={[s.input, { paddingRight: 60, borderColor: theme.bgBorder, color: theme.text, backgroundColor: theme.bgSurface }, errors.confirm && s.inputError]}
              />
              <TouchableOpacity
                onPress={() => setShowConfirm(v => !v)}
                style={s.eyeBtn}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Text style={[s.eyeBtnText, { color: theme.textFaint }]}>{showConfirm ? t('hide_password') : t('show_password')}</Text>
              </TouchableOpacity>
            </View>
            {errors.confirm && <Text style={s.errorText}>{errors.confirm}</Text>}

            <TouchableOpacity
              onPress={onReset}
              disabled={busy}
              activeOpacity={0.85}
              style={[s.submitBtn, busy && { opacity: 0.6 }]}
            >
              {busy
                ? <ActivityIndicator color="#fff" size="small" />
                : <Text style={s.submitText}>{t('forgot_reset_cta')}</Text>}
            </TouchableOpacity>

            <TouchableOpacity onPress={() => setStep(1)} style={s.altBtn}>
              <Text style={[s.altText, { color: theme.textFaint }]}>{t('forgot_change_email_link')}</Text>
            </TouchableOpacity>
          </View>
        )}

        <TouchableOpacity
          onPress={() => {
            // rootReplace('Login') = remplace ForgotPassword par Login dans
            // le stack global. goBack ne marche pas fiablement dans modal stack.
            const em = email.trim().toLowerCase();
            rootReplace('Login', em ? { prefillEmail: em } : undefined);
          }}
          style={s.backBtn}
        >
          <Text style={[s.backText, { color: theme.textFaint }]}>{t('back_to_signin')}</Text>
        </TouchableOpacity>
      </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe: { flex: 1 },
  scroll: { padding: spacing.xl, gap: 16, paddingBottom: 40 },
  header: { alignItems: 'center', gap: 8, marginTop: 16, marginBottom: 8 },
  icon: { fontSize: 36 },
  title: { fontSize: 22, fontWeight: '700', letterSpacing: 0.3 },
  subtitle: { fontSize: 13, textAlign: 'center', maxWidth: 360, lineHeight: 18 },

  form: { gap: 8 },
  label: { fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', marginTop: 8, marginBottom: 4, fontWeight: '600' },
  input: {
    height: 46,
    borderWidth: 1,
    borderRadius: radius.md,
    paddingHorizontal: 14,
    fontSize: 14,
  },
  codeInputWide: {
    height: 56,
    fontSize: 22,
    letterSpacing: 6,
    textAlign: 'center',
    fontWeight: '600',
  },
  // v1.12.118 — erreurs inline (champs rouges)
  inputError: {
    borderColor: '#C62828',
    borderWidth: 1.5,
    backgroundColor: '#FFF5F5',
  },
  errorText: {
    fontSize: 11,
    color: '#C62828',
    marginTop: 4,
    marginLeft: 2,
    fontWeight: '500',
  },
  eyeBtn: { position: 'absolute', right: 12, top: 0, bottom: 0, justifyContent: 'center' },
  eyeBtnText: { fontSize: 11, fontWeight: '600', letterSpacing: 0.4 },

  submitBtn: {
    height: 50,
    borderRadius: radius.md,
    backgroundColor: '#9B7B5A',
    alignItems: 'center', justifyContent: 'center',
    marginTop: 12,
    ...shadow.sm,
  },
  submitText: { color: '#fff', fontSize: 14, fontWeight: '700', letterSpacing: 1 },

  altBtn: { alignSelf: 'center', paddingVertical: 8 },
  altText: { fontSize: 12 },

  backBtn: { alignSelf: 'center', paddingVertical: 14, marginTop: 8 },
  backText: { fontSize: 13, color: '#9B7B5A' },
});
